import {
  MEMBERSHIP_TYPES,
  RESTRICTED_USER_ROLES,
  USER_ATTORNEY_ROLES,
  USER_ROLES,
} from '@/constants'
import { UserOrganizationQuery } from '@/gql/systemApi'
import { useGetVettedAttorneysInOrgQuery } from '@/gql/appApi'
import { useMemo, useCallback } from 'react'
import { ApolloError } from '@apollo/client'

interface IUseGetCaseManagerRestrictionsProps {
  userMe: UserOrganizationQuery | undefined
  userMeLoading: boolean
  userMeError: ApolloError | undefined
  refetchUserMe: () => void
}

interface RoleRestrictionsState {
  shouldBlock: boolean
  isLoading: boolean
  hasError: boolean
  errorMessage?: string
  refetch: () => void
  isRestrictedUser: boolean
}

const useGetRoleRestrictions = ({
  userMe,
  userMeLoading,
  userMeError,
  refetchUserMe,
}: IUseGetCaseManagerRestrictionsProps): RoleRestrictionsState => {
  const orgId = userMe?.users_me?.current_organization?.[0]?.organization_id?.id
  const userId = userMe?.users_me?.id
  const isCaseManager =
    userMe?.users_me?.role?.id === USER_ROLES.CaseManagerUser

  const shouldFetchAttorneys =
    !userMeLoading && !userMeError && isCaseManager && userId && orgId

  const isRestrictedUser = RESTRICTED_USER_ROLES.includes(
    userMe?.users_me?.role?.id || ''
  )

  const {
    data: validAttorneysData,
    loading: validAttorneysLoading,
    error: validAttorneysError,
    refetch: refetchAttorneys,
  } = useGetVettedAttorneysInOrgQuery({
    variables: {
      filter: {
        _and: [
          {
            _or: [
              {
                membership_type: {
                  id: {
                    _eq: MEMBERSHIP_TYPES.Vetted,
                  },
                },
              },
              {
                membership_type: {
                  id: {
                    _eq: MEMBERSHIP_TYPES.Qualified,
                  },
                },
              },
            ],
            user_id: {
              current_organization: {
                organization_id: {
                  id: {
                    _eq: orgId,
                  },
                },
              },
            },
          },
          {
            user_id: {
              id: {
                _neq: userId,
              },
            },
          },
          {
            user_id: {
              role: {
                id: {
                  _in: USER_ATTORNEY_ROLES,
                },
              },
            },
          },
        ],
      },
    },
    fetchPolicy: 'network-only',
    skip: !shouldFetchAttorneys,
  })

  const validAttorneys = useMemo(
    () => validAttorneysData?.attorney?.map(attorney => attorney.id) || [],
    [validAttorneysData]
  )

  const refetch = useCallback(async () => {
    await refetchUserMe()
    if (shouldFetchAttorneys) {
      await refetchAttorneys()
    }
  }, [refetchUserMe, refetchAttorneys, shouldFetchAttorneys])

  return useMemo((): RoleRestrictionsState => {
    const isLoading =
      userMeLoading || (shouldFetchAttorneys && validAttorneysLoading)

    const hasUserError = userMeError !== undefined
    const hasAttorneysError =
      shouldFetchAttorneys && validAttorneysError !== undefined
    const hasError = hasUserError || hasAttorneysError

    let errorMessage: string | undefined
    if (hasUserError) {
      errorMessage = 'Failed to load user information'
      // eslint-disable-next-line no-console
      console.error('Failed to load user information', userMeError)
    } else if (hasAttorneysError) {
      errorMessage = 'Failed to load valid attorneys information'
      // eslint-disable-next-line no-console
      console.error(
        'Failed to load valid attorneys information',
        validAttorneysError
      )
    }

    if (isLoading || hasError) {
      return {
        shouldBlock: true,
        isLoading: !!isLoading,
        hasError: !!hasError,
        errorMessage: errorMessage || undefined,
        refetch,
        isRestrictedUser,
      }
    }

    if (isRestrictedUser) {
      return {
        shouldBlock: true,
        isLoading: false,
        hasError: false,
        refetch,
        isRestrictedUser,
      }
    }

    // For non-case-managers, don't block
    if (!isCaseManager && !isRestrictedUser) {
      return {
        shouldBlock: false,
        isLoading: false,
        hasError: false,
        refetch,
        isRestrictedUser,
      }
    }

    if (!validAttorneysData || !validAttorneysData?.attorney) {
      return {
        shouldBlock: true,
        isLoading: false,
        hasError: true,
        errorMessage: 'No attorney data returned from query',
        refetch,
        isRestrictedUser,
      }
    }

    const shouldBlock = validAttorneys.length === 0

    return {
      shouldBlock,
      isLoading: false,
      hasError: false,
      refetch,
      isRestrictedUser,
    }
  }, [
    userMeLoading,
    userMeError,
    shouldFetchAttorneys,
    validAttorneysLoading,
    validAttorneysError,
    validAttorneys.length,
    validAttorneysData,
    refetch,
    isCaseManager,
    isRestrictedUser,
  ])
}

export default useGetRoleRestrictions
